/* eslint-disable @next/next/no-img-element */

"use client";

import React, { useState, useEffect } from 'react';
import Image from 'next/image';
import { useRouter, useSearchParams } from 'next/navigation';
import { Eye, Settings2, Search, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { PhotoIcon, PaperAirplaneIcon, Squares2X2Icon } from "@heroicons/react/24/outline";
import { useInView } from 'react-intersection-observer'; 
import { useThemes, ThemeItem } from '@/hooks/theme/useThemes'; 
import { useEventManagement } from '@/hooks/useEventManagement';
import { apiClient } from '@/lib/api'; 
import { assets } from "@/utils/assets"; 
import { Modal } from '@/components/ui/modal/Modal';
import { useLocale } from "@/context/LocaleContext";

const getPlainImage = (theme: ThemeItem): string | null => {
  if (theme.jenis_acara_tema && Array.isArray(theme.jenis_acara_tema) && theme.jenis_acara_tema.length > 0) {
    const imgSampul = theme.jenis_acara_tema[0].image_sampul;
    if (imgSampul && typeof imgSampul === 'string' && imgSampul.trim() !== '') {
      return imgSampul.startsWith('http') ? imgSampul : assets(imgSampul);
    }
  }

  // Fallback ke live_demo jika image_sampul kosong
  if (theme.live_demo && typeof theme.live_demo === 'string' && theme.live_demo.trim() !== '') {
    return theme.live_demo.startsWith('http') ? theme.live_demo : assets(theme.live_demo);
  }
  
  return null; 
};

const getIconUrl = (iconPath?: string) => {
  if (!iconPath) return "/images/icons/acara-lainnya.svg";
  if (iconPath.startsWith("http")) return iconPath;
  const safePath = encodeURI(iconPath);
  return assets(safePath).replace(/([^:]\/)\/+/g, "$1");
};

function ThemeCard({ theme, customCoverImage, isRecommend }: { theme: ThemeItem, customCoverImage?: string, isRecommend?: boolean }) {
  const { t, locale } = useLocale(); 
  
  const localePrefix = locale === "en" ? "/en" : "";
  
  let initialImg = null;
  if (isRecommend) {
      if (customCoverImage && customCoverImage.trim() !== "") {
          initialImg = customCoverImage.startsWith('http') ? customCoverImage : assets(customCoverImage);
      } else {
          initialImg = getPlainImage(theme);
      }
  } else {
      initialImg = getPlainImage(theme);
  }

  const [imgSrc] = useState<string | null>(initialImg);
  const [imgError, setImgError] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);
  const router = useRouter();

  const handlePreviewClick = () => {
    if (theme.previews && theme.previews.length > 0) {
      setPreviewIndex(0);
      setIsPreviewOpen(true);
    } 
    else {
      const demoLink = theme.url_demo || theme.live_demo;
      
      if (demoLink) {
        const validLink = demoLink.startsWith('http') ? demoLink : `https://${demoLink}`;
        window.open(validLink, '_blank', 'noopener,noreferrer');
      } else {
        alert(t("LandingPage.preview_not_available") || "Mohon maaf, Preview/Demo belum tersedia untuk tema ini.");
      }
    }
  };

  const nextPreview = () => {
    if (!theme.previews) return;
    setPreviewIndex((prev) => (prev === theme.previews.length - 1 ? 0 : prev + 1));
  };

  const prevPreview = () => {
    if (!theme.previews) return;
    setPreviewIndex((prev) => (prev === 0 ? theme.previews.length - 1 : prev - 1));
  };

  const handleUseTheme = () => {
    router.push(`${localePrefix}/add-event?idtema=${theme.id}`);
  };

  return (
    <>
      <div className="rounded-[20px] overflow-hidden border-2 border-gray-100 shadow-[0_2px_15px_rgba(0,0,0,0.04)] hover:shadow-[0_10px_30px_rgba(0,0,0,0.08)] hover:-translate-y-1 transform transition-all duration-300 bg-white flex flex-col h-full">
        <div className="relative w-full aspect-square rounded-[20px] overflow-hidden isolate bg-gray-50 flex items-center justify-center">
          {imgError || !imgSrc ? (
            <div className="flex flex-col items-center gap-2 opacity-50">
              <PhotoIcon className="w-12 h-12 text-gray-400" strokeWidth={1.5} />
              <span className="text-[10px] font-bold text-gray-400 tracking-widest uppercase">No Preview</span>
            </div>
          ) : (
            <Image
              src={imgSrc}
              alt={theme.template_name || "Theme Cover"}
              fill
              className="object-cover object-top"
              unoptimized 
              onError={() => {
                setImgError(true);
              }} 
            />
          )}
        </div>

        <div className="bg-white p-3 md:p-4 flex flex-col flex-1 justify-end">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-semibold text-gray-900 text-[13px] md:text-[15px] truncate pr-2">
                {theme.template_name || (t("LandingPage.tema_tanpa_nama") || "Tema Tanpa Nama")}
            </h3>
            <span className="font-semibold text-[#E85C57] text-[13px] md:text-[14px]">
                {theme.custom ? (t("CustomForm.premium") || 'Premium') : (t("LandingPage.gratis") || 'Gratis')}
            </span>
          </div>
          
          <div className="flex flex-col xl:flex-row gap-1.5 md:gap-2 w-full justify-between">
            <button onClick={handlePreviewClick} className="flex-1 bg-[#F5F5F5] hover:bg-gray-200 text-[#2A2A2A] rounded-full py-2 px-2 text-[11px] lg:text-[11px] font-semibold flex items-center justify-center gap-1.5 transition-colors duration-200 whitespace-nowrap">
              <Eye size={14} strokeWidth={2.5} className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> 
              {t("LandingPage.preview_btn") || "Preview"}
            </button>
            <button 
              onClick={handleUseTheme}
              className="flex-1 bg-[#1A1A1A] hover:bg-black text-white rounded-full py-2 px-2 text-[11px] lg:text-[11px] font-semibold flex items-center justify-center gap-1.5 transition-colors duration-200 whitespace-nowrap"
            >
              <PaperAirplaneIcon className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-white" /> 
              {t("LandingPage.gunakan_tema_btn") || "Gunakan Tema"}
            </button>
          </div>
        </div>
      </div>

      {isPreviewOpen && theme.previews && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4" onClick={() => setIsPreviewOpen(false)}>
          <button onClick={() => setIsPreviewOpen(false)} className="absolute top-6 right-6 md:top-8 md:right-8 z-[1000] bg-white/20 hover:bg-white/40 rounded-full p-2.5 text-white transition-colors">
            <X size={24} />
          </button>
          <div className="relative w-full max-w-[420px] aspect-[9/16] md:aspect-[3/4] bg-transparent flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
             <Image src={theme.previews[previewIndex].template_preview_img.startsWith('http') ? theme.previews[previewIndex].template_preview_img : assets(theme.previews[previewIndex].template_preview_img)} alt={`Preview ${previewIndex + 1}`} fill className="object-contain" unoptimized />
             {theme.previews.length > 1 && (
               <>
                  <button onClick={prevPreview} className="absolute -left-5 md:-left-16 top-1/2 -translate-y-1/2 text-white bg-white/20 hover:bg-white/40 rounded-full p-2.5 transition-colors shadow-lg"><ChevronLeft size={24}/></button>
                  <button onClick={nextPreview} className="absolute -right-5 md:-right-16 top-1/2 -translate-y-1/2 text-white bg-white/20 hover:bg-white/40 rounded-full p-2.5 transition-colors shadow-lg"><ChevronRight size={24}/></button>
                  <div className="absolute -bottom-10 left-1/2 -translate-x-1/2 text-white text-sm font-medium tracking-wide">{previewIndex + 1} / {theme.previews.length}</div>
               </>
             )}
          </div>
        </div>
      )}
    </>
  );
}

function ThemeCardSkeleton() {
  return (
    <div className="rounded-[20px] overflow-hidden border-2 border-gray-50 bg-white flex flex-col h-full animate-pulse">
      <div className="w-full aspect-square bg-gray-100 rounded-[20px]"></div>
      <div className="p-4 flex flex-col flex-1 justify-end gap-4 mt-2">
         <div className="flex justify-between items-center">
           <div className="h-4 bg-gray-200 rounded-md w-1/2"></div><div className="h-4 bg-gray-200 rounded-md w-1/4"></div>
         </div>
         <div className="flex flex-col xl:flex-row gap-2">
           <div className="h-8 bg-gray-200 rounded-full w-full"></div><div className="h-8 bg-gray-200 rounded-full w-full"></div>
         </div>
      </div>
    </div>
  );
}

export default function ThemeFeature() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { t } = useLocale(); 
  
  const { eventTypes, fetchEventTypes, isLoadingList: isLoadingEvents } = useEventManagement();
  const [categories, setCategories] = useState<{id: number, nama_category: string}[]>([]);
  const [colors, setColors] = useState<{id: number, nama_warna: string, code_warna: string}[]>([]);
  const [isFiltersLoaded, setIsFiltersLoaded] = useState(false);

  const urlEvent = searchParams.get('event') || '';
  const urlType = searchParams.get('type') || '';
  const urlColor = searchParams.get('color') || '';
  const urlSearch = searchParams.get('search') || '';

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [searchValue, setSearchValue] = useState(urlSearch);
  const [selectedEvent, setSelectedEvent] = useState(urlEvent);
  const [selectedStyle, setSelectedStyle] = useState(urlType);
  const [selectedColor, setSelectedColor] = useState(urlColor);

  useEffect(() => {
      setSearchValue(urlSearch);
      setSelectedEvent(urlEvent);
      setSelectedStyle(urlType);
      setSelectedColor(urlColor);
  }, [urlEvent, urlType, urlColor, urlSearch]);

  const resolvedAcaraId = eventTypes.find(e => e.jenis_acara.toLowerCase() === urlEvent.toLowerCase())?.id?.toString() || '';
  const resolvedCatId = categories.find(c => c.nama_category.toLowerCase() === urlType.toLowerCase())?.id?.toString() || '';
  const resolvedWarnaId = colors.find(c => c.nama_warna.toLowerCase() === urlColor.toLowerCase())?.id?.toString() || '';
  
  const isReadyToFetch = isFiltersLoaded && !isLoadingEvents;

  const { data, allThemes, isLoading, isLoadingMore, hasMore, error, loadMore } = useThemes(
    resolvedAcaraId,
    resolvedCatId,
    resolvedWarnaId,
    urlSearch,
    isReadyToFetch
  );

  useEffect(() => {
    let isMounted = true;
    const loadAllFilters = async () => {
      try {
        const [catRes, colRes] = await Promise.all([
            apiClient.get('/api/list/get-tema-filter'),
            apiClient.get('/api/list/warna-tema')
        ]);
        
        if (isMounted) {
            setCategories(catRes.data?.data?.category || []);
            
            const rawColors = colRes.data?.data || [];
            const uniqueColorsMap = new Map();
            rawColors.forEach((item: { warna?: { id: number | string } }) => {
                if (item.warna && !uniqueColorsMap.has(item.warna.id)) {
                    uniqueColorsMap.set(item.warna.id, item.warna);
                }
            });
            setColors(Array.from(uniqueColorsMap.values()));
        }
      } catch (err) {
        console.error("Gagal load master filter:", err);
      } finally {
        if (isMounted) setIsFiltersLoaded(true);
      }
    };

    fetchEventTypes();
    loadAllFilters();
    
    return () => { isMounted = false; };
  }, [fetchEventTypes]);

  const { ref: observerRef, inView } = useInView({ threshold: 0.1, rootMargin: "200px" });

  useEffect(() => {
    if (inView && hasMore && !isLoading && !isLoadingMore && isReadyToFetch) {
      loadMore();
    }
  }, [inView, hasMore, isLoading, isLoadingMore, loadMore, isReadyToFetch]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const current = new URLSearchParams(Array.from(searchParams.entries()));
    if (searchValue) current.set('search', searchValue); else current.delete('search');
    current.delete('page'); 
    router.push(`/templates?${current.toString()}`);
  };

  const applyFilter = () => {
    const current = new URLSearchParams(Array.from(searchParams.entries()));
    if (selectedEvent) current.set('event', selectedEvent); else current.delete('event');
    if (selectedStyle) current.set('type', selectedStyle); else current.delete('type');
    if (selectedColor) current.set('color', selectedColor); else current.delete('color');
    current.delete('page'); 
    
    router.push(`/templates?${current.toString()}`);
    setIsFilterOpen(false);
  };

  const resetFilter = () => {
    setSelectedEvent('');
    setSelectedStyle('');
    setSelectedColor('');
    const current = new URLSearchParams(Array.from(searchParams.entries()));
    current.delete('event');
    current.delete('type');
    current.delete('color');
    current.delete('page');
    router.push(`/templates?${current.toString()}`);
    setIsFilterOpen(false);
  };

  const hasActiveFilters = urlColor || urlEvent || urlType;

  return (
    <div className="bg-[#fafafa] min-h-screen pb-20 font-sans">
      
      {/* HEADER BANNER */}
      <div className="relative bg-[#E85C57] pt-28 pb-16 md:pt-36 md:pb-20 px-5 lg:px-12 w-full overflow-hidden">
        <div className="w-full max-w-7xl mx-auto flex flex-col md:flex-row items-center relative z-10">
          <div className="w-full md:w-[55%] text-center md:text-left mb-10 md:mb-0 lg:pr-8">
            <h1 className="text-white text-[2.2rem] sm:text-[3rem] md:text-[3.2rem] lg:text-[4rem] font-bold leading-[1.05] mb-4 tracking-tight">
              {t("LandingPage.theme_banner_title_1") || "Pilih Serta Sesuaikan"}
              <br className="hidden sm:block" /> 
              {t("LandingPage.theme_banner_title_2") || "Tema Undanganmu"}
            </h1>
            <p className="text-white/95 text-[1rem] md:text-[1.15rem] lg:text-[1.25rem] font-medium leading-relaxed max-w-[95%] mx-auto md:mx-0">
              {t("LandingPage.theme_banner_desc") || "Gak perlu capek edit-edit tampilan, temanya bisa kalian ubah sesuka hati!"}
            </p>
          </div>
          <div className="w-full md:w-[45%] flex justify-center md:justify-end relative h-[220px] sm:h-[300px] md:h-[350px] lg:h-[420px]">
             <Image src="/images/cta/theme-feature.webp" fill className="object-contain md:object-right" alt="Themes" priority/>
          </div>
        </div>
      </div>

      {/* SEARCH & FILTER BAR */}
      <div className="w-[92%] md:w-[100%] max-w-[1250px] mx-auto relative z-20 -mt-8 md:-mt-11">
        <form onSubmit={handleSearch} className="bg-white rounded-2xl md:rounded-[24px] p-2 md:p-3 flex items-center shadow-[0_15px_40px_rgba(0,0,0,0.1)] border border-gray-100">
          <button 
            type="button"
            onClick={() => setIsFilterOpen(true)} 
            className="flex items-center gap-2 px-4 py-3 md:py-4 text-[#2A2A2A] font-semibold text-[15px] hover:bg-gray-50 rounded-xl transition-colors border-r border-gray-100 mr-2 md:mr-3"
          >
            <Settings2 size={20} className="text-gray-600"/>
            <span className="hidden sm:block">{t("LandingPage.filters_btn") || "Filters"}</span>
          </button>
          <input 
            type="text" 
            placeholder={t("LandingPage.search_theme_placeholder") || "Masukkan Nama Tema"}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            className="flex-1 bg-transparent outline-none px-3 text-[15px] text-gray-700 placeholder-gray-400"
          />
          <button type="submit" className="w-12 h-12 md:w-16 md:h-16 bg-[#E85C57] hover:bg-[#D44A45] rounded-xl md:rounded-[18px] flex items-center justify-center text-white transition-colors flex-shrink-0 shadow-sm">
            <Search size={24} strokeWidth={2.5} />
          </button>
        </form>

        {hasActiveFilters && (
          <div className="mt-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 bg-white px-4 py-3 rounded-xl shadow-sm border border-gray-100 mx-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[13px] md:text-[14px] text-gray-500 font-medium mr-1">
                {t("LandingPage.active_filter_label") || "Sedang menerapkan filter:"}
              </span>
              
              {urlColor && (
                <span className="bg-gray-50 border border-gray-200 text-gray-700 px-3 py-1 rounded-full text-[12px] font-semibold">
                  {t("LandingPage.filter_color") || "Warna"}: {urlColor}
                </span>
              )}
              {urlEvent && (
                <span className="bg-gray-50 border border-gray-200 text-gray-700 px-3 py-1 rounded-full text-[12px] font-semibold">
                  {t("LandingPage.filter_event") || "Jenis Acara"}: {urlEvent}
                </span>
              )}
              {urlType && (
                <span className="bg-gray-50 border border-gray-200 text-gray-700 px-3 py-1 rounded-full text-[12px] font-semibold">
                  {t("LandingPage.filter_style") || "Style"}: {urlType}
                </span>
              )}
            </div>
            
            <button 
              onClick={resetFilter}
              className="text-[#E85C57] text-[12px] md:text-[13px] font-bold hover:underline whitespace-nowrap self-end sm:self-auto"
            >
              {t("LandingPage.clear_filter_btn") || "Hapus Filter"}
            </button>
          </div>
        )}
      </div>

      {/* LIST TEMA */}
      <div className="max-w-[1300px] mx-auto px-5 lg:px-8 mt-16 space-y-16">
        
        {(!isReadyToFetch || isLoading) && (
           <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6 lg:gap-8">
              {[1, 2, 3, 4, 5, 6, 7, 8].map(i => <ThemeCardSkeleton key={i} />)}
           </div>
        )}
        
        {error && <div className="text-center py-20 text-red-500 font-medium">{error}</div>}

        {isReadyToFetch && !isLoading && !error && data && (
          <>
            {!urlSearch && data.recommend && data.recommend.length > 0 && data.recommend.map((recCategory) => {
              if (!recCategory.list || recCategory.list.length === 0) return null;

              return (
                <section key={`rec-cat-${recCategory.id}`}>
                  <h2 className="text-[1.3rem] md:text-[1.5rem] font-bold text-[#2A2A2A] mb-6 md:mb-8">
                    {t("LandingPage.recommendation_label") || "Rekomendasi"} {recCategory.template_recommend}
                  </h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6 lg:gap-8">
                    {recCategory.list.slice(0, 4).map((item: { template: ThemeItem; sampul_image?: string }) => (
                      <ThemeCard 
                        key={`rec-item-${item.template.id}`} 
                        theme={item.template} 
                        customCoverImage={item.sampul_image} 
                        isRecommend={true} 
                      />
                    ))}
                  </div>
                </section>
              );
            })}

            <section>
              <div className="flex justify-between items-end mb-6 md:mb-8">
                <h2 className="text-[1.3rem] md:text-[1.5rem] font-bold text-[#2A2A2A]">
                  {urlSearch ? (
                    <>{t("LandingPage.search_result_label") || "Hasil pencarian:"} <span className="text-[#E85C57]">&lsquo;{urlSearch}&rsquo;</span></>
                  ) : (
                    t("LandingPage.all_themes_label") || "Semua Tema"
                  )}
                </h2>
                <span className="text-gray-400 font-medium text-[13px] md:text-[14px]">{data.pagination.meta.total}+ {t("LandingPage.design_label") || "Desain"}</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6 lg:gap-8">
                {allThemes.map((theme) => (
                  <ThemeCard key={`all-${theme.id}`} theme={theme} isRecommend={false} />
                ))}
                {isLoadingMore && (
                  <><ThemeCardSkeleton /><ThemeCardSkeleton /><ThemeCardSkeleton /><ThemeCardSkeleton /></>
                )}
              </div>
              {hasMore && (<div ref={observerRef} className="h-10 w-full mt-4"></div>)}
              {allThemes.length === 0 && (
                <div className="text-center py-20 bg-white rounded-3xl border border-gray-100">
                  <PhotoIcon className="w-16 h-16 text-gray-200 mx-auto mb-3" />
                  <p className="text-gray-500 font-medium mb-1">{t("LandingPage.theme_not_found") || "Tema tidak ditemukan."}</p>
                  <p className="text-gray-400 text-sm">{t("LandingPage.theme_not_found_desc") || "Coba gunakan kata kunci atau filter lain."}</p>
                </div>
              )}
            </section>
          </>
        )}
      </div>

      <Modal
        open={isFilterOpen}
        onOpenChange={setIsFilterOpen}
        title={t("LandingPage.filter_modal_title") || "Filter Tema"}
        maxWidth="max-w-4xl"
        desktopContentClassName="md:max-w-[700px] w-full" 
      >
        <div className="p-4 md:p-5 space-y-5 max-h-[75vh] overflow-y-auto w-full">
          
          <div className="w-full">
            <h3 className="font-bold text-[#2A2A2A] text-[13px] md:text-[14px] mb-2.5">{t("LandingPage.filter_category_label") || "Kategori Acara"}</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 w-full">
              <button
                onClick={() => setSelectedEvent('')}
                className={`flex items-center gap-2 p-2 px-3 rounded-[10px] border-[1.5px] transition-colors w-full ${
                  selectedEvent === '' ? 'bg-[#E85C57] text-white border-[#E85C57]' : 'bg-white border-gray-200 text-gray-700 hover:border-[#E85C57] hover:text-[#E85C57]'
                }`}
              >
                <Squares2X2Icon className="w-4 h-4 flex-shrink-0" />
                <span className="text-[11px] md:text-[12px] font-semibold leading-tight text-left">{t("LandingPage.all_categories") || "Semua Kategori"}</span>
              </button>
              
              {eventTypes.map(ev => (
                <button
                  key={ev.id}
                  onClick={() => setSelectedEvent(ev.jenis_acara)}
                  className={`flex items-center gap-2 p-2 px-3 rounded-[10px] border-[1.5px] transition-colors w-full ${
                    selectedEvent === ev.jenis_acara ? 'bg-[#E85C57] text-white border-[#E85C57]' : 'bg-white border-gray-200 text-gray-700 hover:border-[#E85C57] hover:text-[#E85C57]'
                  }`}
                >
                  <img 
                    src={getIconUrl(ev.icon)} 
                    className={`w-4 h-4 object-contain flex-shrink-0 transition-all ${selectedEvent === ev.jenis_acara ? 'brightness-0 invert' : ''}`} 
                    alt={ev.jenis_acara} 
                    onError={(e) => {
                        const target = e.currentTarget;
                        if (!target.src.includes("acara-lainnya.svg")) {
                            target.src = "/images/icons/acara-lainnya.svg";
                            target.srcset = ""; 
                        }
                    }}
                  />
                  <span className="text-[11px] md:text-[12px] font-semibold leading-tight text-left truncate">{ev.jenis_acara}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="w-full">
            <h3 className="font-bold text-[#2A2A2A] text-[13px] md:text-[14px] mb-2.5">{t("LandingPage.filter_style_label") || "Tipe Style"}</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 w-full">
              <button
                onClick={() => setSelectedStyle('')}
                className={`py-1.5 md:py-2 px-3 rounded-[10px] border-[1.5px] font-semibold text-[11px] md:text-[12px] transition-colors w-full text-center ${
                  selectedStyle === '' ? 'bg-[#E85C57] text-white border-[#E85C57]' : 'bg-white border-gray-200 text-gray-700 hover:border-[#E85C57] hover:text-[#E85C57]'
                }`}
              >
                {t("LandingPage.all_styles") || "Semua Tipe"}
              </button>
              
              {categories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedStyle(cat.nama_category)}
                  className={`py-1.5 md:py-2 px-3 rounded-[10px] border-[1.5px] font-semibold text-[11px] md:text-[12px] transition-colors w-full text-center truncate ${
                    selectedStyle === cat.nama_category ? 'bg-[#E85C57] text-white border-[#E85C57]' : 'bg-white border-gray-200 text-gray-700 hover:border-[#E85C57] hover:text-[#E85C57]'
                  }`}
                >
                  {cat.nama_category}
                </button>
              ))}
            </div>
          </div>

          <div className="w-full">
            <h3 className="font-bold text-[#2A2A2A] text-[13px] md:text-[14px] mb-2.5">{t("LandingPage.filter_color_label") || "Warna"}</h3>
            <div className="flex flex-wrap gap-2 md:gap-2.5">
              {colors.map(col => (
                <button
                  key={col.id}
                  onClick={() => setSelectedColor(col.nama_warna)}
                  style={{ backgroundColor: col.code_warna }}
                  className={`w-[32px] h-[32px] md:w-[36px] md:h-[36px] rounded-lg border-2 transition-all duration-200 shadow-sm ${
                    selectedColor === col.nama_warna 
                    ? 'border-gray-900 scale-110 ring-2 ring-offset-2 ring-gray-900/30' 
                    : 'border-gray-200/50 hover:scale-110'
                  }`}
                  title={col.nama_warna}
                  aria-label={`Pilih warna ${col.nama_warna}`}
                />
              ))}
            </div>
          </div>
          
        </div>

        <div className="p-3 md:px-5 md:py-4 border-t border-gray-100 flex justify-between gap-3 bg-gray-50/50 rounded-b-[24px]">
          <button 
            onClick={resetFilter} 
            className="px-5 md:px-6 py-2 bg-[#F5F5F5] hover:bg-gray-200 text-[#2A2A2A] font-bold rounded-[10px] text-[12px] md:text-[13px] transition-colors"
          >
            {t("LandingPage.reset_btn") || "Reset"}
          </button>
          <button 
            onClick={applyFilter} 
            className="px-6 md:px-8 py-2 bg-[#E85C57] hover:bg-[#D44A45] text-white font-bold rounded-[10px] text-[12px] md:text-[13px] transition-colors shadow-sm"
          >
            {t("LandingPage.apply_filter_btn") || "Terapkan Filter"}
          </button>
        </div>
      </Modal>

    </div>
  );
}